import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getImagingQueue } from '../../api/orders.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import QueueTabs, { makeClassifier } from '../../components/QueueTabs.jsx'
import { formatDateTime, statusColor } from '../../utils/format.js'

// Imaging orders are "done" once the centre has marked them completed
// or ready for collection. Anything else (pending, in_progress) is a
// New tab item demanding the radiologist's attention.
const classifyImagingOrder = makeClassifier({
  doneStatuses: ['completed', 'ready', 'fulfilled'],
})

/**
 * Radiologist's queue. Each row is structured around the patient — name
 * leads, with age/sex/phone on a small line directly below — so the
 * radiologist can confirm the recipient before any study is run.
 * Imaging is a high-risk modality (radiation, contrast, pregnancy
 * considerations); the recipient banner is a safety surface, not a
 * decoration.
 *
 * Hierarchy in each row:
 *   Patient name              ← bold, primary
 *     · age · sex · phone     ← small identity strip
 *   Studies requested         ← compact chips
 *   Status · Doctor · Time · Indication ← tertiary metadata
 *   Open ▸                    ← right-aligned action
 */
export default function RadiologyDashboard() {
  const q = useQuery({
    queryKey: ['radiology-queue'],
    queryFn: getImagingQueue,
    refetchInterval: 15_000,
  })

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Radiology queue"
          subtitle="Confirm patient identity (name + DOB) before any study is performed."
          right={
            <div className="flex items-center gap-3">
              <Link to="/radiology/catalogue" className="text-xs text-wantok-700 underline">My catalogue</Link>
              <button onClick={() => q.refetch()} className="text-xs text-wantok-700 underline">Refresh</button>
            </div>
          }
        />
        {q.error && <ErrorBanner error={q.error} onRetry={() => q.refetch()} />}
        {q.isLoading ? (
          <div className="py-3 text-sm text-slate-400">Loading…</div>
        ) : (
          <QueueTabs
            rows={q.data || []}
            classifyRow={classifyImagingOrder}
            keyExtractor={(io) => io.id}
            renderRow={(io) => <ImagingQueueRow key={io.id} order={io} />}
            emptyMessages={{
              new: 'No pending studies. New imaging orders will land here.',
              processed: 'No studies completed in the last 30 days.',
              archived: 'No older studies in the archive.',
            }}
          />
        )}
      </Card>
    </div>
  )
}

/* ---- Single queue row ---------------------------------------------------- */

function ImagingQueueRow({ order }) {
  const patient = order.patient_name || 'Unknown patient'
  const age = ageFromDob(order.patient_dob)
  const sex = (order.patient_sex || '').trim()

  // Studies as compact chips, capped to 3 + "+N more" so a wide order
  // doesn't blow out the row height. Imaging orders rarely exceed 2 or
  // 3 studies in one go, so this almost always shows them all.
  const allStudies = (order.items || []).map((i) => i.study_name).filter(Boolean)
  const fallback = allStudies.length === 0 && order.imaging_type ? [order.imaging_type] : []
  const visibleStudies = (allStudies.length ? allStudies : fallback).slice(0, 3)
  const hiddenCount = (allStudies.length || fallback.length) - visibleStudies.length

  // Result status drives a small coloured dot — at-a-glance lifecycle
  // indicator that doesn't require parsing the status pill.
  const resultStatus = (order.result_status || 'pending').toLowerCase()
  const dot =
    resultStatus === 'final'       ? 'bg-emerald-500' :
    resultStatus === 'preliminary' ? 'bg-sky-500'     :
                                     'bg-slate-300'

  return (
    <li className="py-3 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1 flex gap-3">
        {/* Status dot */}
        <div className={`shrink-0 mt-1.5 w-2 h-2 rounded-full ${dot}`} aria-hidden="true" />

        <div className="min-w-0">
          {/* Patient name — primary */}
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-semibold text-slate-800 text-base truncate">
              {patient}
            </span>
            {/* Identity strip */}
            <span className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
              {age !== null && <span>{age} y</span>}
              {sex && <span className="capitalize">{sex}</span>}
              {order.patient_phone && <span>☎ {order.patient_phone}</span>}
              {!age && !sex && !order.patient_phone && (
                <span className="text-slate-400 italic">no demographics on file</span>
              )}
            </span>
          </div>

          {/* Studies requested — compact chips */}
          <div className="mt-1.5 text-sm text-slate-700 flex flex-wrap gap-1">
            {visibleStudies.length === 0 && (
              <span className="text-slate-400 italic">no studies listed</span>
            )}
            {visibleStudies.map((s, i) => (
              <span
                key={i}
                className="inline-block bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-[11px]"
              >
                {s}
              </span>
            ))}
            {hiddenCount > 0 && (
              <span className="inline-block text-[11px] text-slate-500 px-1 py-0.5">
                +{hiddenCount} more
              </span>
            )}
          </div>

          {/* Tertiary line — status, ordering doctor, time, indication */}
          <div className="mt-1.5 text-[11px] text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={`uppercase border rounded-full px-2 py-0.5 ${statusColor(order.status)}`}>
              {order.status}
            </span>
            {order.doctor_name && (
              <span>Ordered by {order.doctor_name}</span>
            )}
            <span>· {formatDateTime(order.created_at)}</span>
            {order.clinical_indication && (
              <span className="truncate max-w-md">· {order.clinical_indication}</span>
            )}
          </div>
        </div>
      </div>

      {/* Action — right-aligned, big-enough click target */}
      <Link
        to={`/radiology/orders/${order.id}`}
        className="shrink-0 self-center text-xs font-semibold text-white bg-wantok-700 hover:bg-wantok-800 rounded px-3 py-1.5 transition"
      >
        Open
      </Link>
    </li>
  )
}

/* ---- Helpers ------------------------------------------------------------- */

function ageFromDob(dob) {
  if (!dob) return null
  const d = new Date(dob)
  if (isNaN(d.getTime())) return null
  const now = new Date()
  let a = now.getFullYear() - d.getFullYear()
  const m = now.getMonth() - d.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1
  return a
}
